import React from 'react'
import { Helmet } from 'react-helmet'
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react'
import { withTranslation } from 'react-i18next'

interface Author {
  '@type'?: 'Person' | 'Organization' | string
  name?: string
}

interface PublisherLogo {
  '@type'?: 'ImageObject' | string
  url?: string
}

interface Publisher {
  '@type'?: 'Organization' | string
  name?: string
  logo?: PublisherLogo
}

interface MainEntityOfPage {
  '@type'?: 'WebPage' | 'CreativeWork' | string
  '@id'?: string
}

export interface StructuredDataSchemaProps {
  '@context'?: string
  '@type'?: 'Article' | 'NewsArticle' | 'BlogPosting' | string
  headline?: string
  image?: string | string[]
  author?: Author
  datePublished?: string
  dateModified?: string
  publisher?: Publisher
  description?: string
  mainEntityOfPage?: MainEntityOfPage
}

interface Props {
  fields?: StructuredDataSchemaProps
}

const StructuredDataSchema: React.FC<Props> = ({ fields }) => {
  // Build the JSON-LD object only from provided values to avoid empty properties
  const schema = React.useMemo(() => {
    const base: Record<string, unknown> = {
      '@context': fields?.['@context'] ?? 'https://schema.org',
      '@type': fields?.['@type'] ?? 'Article',
    }

    if (fields?.headline) base.headline = fields.headline
    if (fields?.image?.length) base.image = fields.image

    if (fields?.author?.name) {
      base.author = {
        '@type': fields.author['@type'] ?? 'Person',
        name: fields.author.name,
      }
    }

    if (fields?.datePublished) base.datePublished = fields.datePublished
    if (fields?.dateModified) base.dateModified = fields.dateModified

    if (fields?.publisher?.name) {
      const publisher: Record<string, unknown> = {
        '@type': fields.publisher['@type'] ?? 'Organization',
        name: fields.publisher.name,
      }

      if (fields.publisher.logo?.url) {
        publisher.logo = {
          '@type': fields.publisher.logo['@type'] ?? 'ImageObject',
          url: fields.publisher.logo.url,
        }
      }
      base.publisher = publisher
    }

    if (fields?.description) base.description = fields.description

    if (fields?.mainEntityOfPage?.['@id']) {
      base.mainEntityOfPage = {
        '@type': fields.mainEntityOfPage['@type'] ?? 'WebPage',
        '@id': fields.mainEntityOfPage['@id'],
      }
    }

    return base
  }, [fields])

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
    </Helmet>
  )
}

StructuredDataSchema.displayName = 'StructuredDataSchema'

export default withSitecoreContext()(withTranslation()(StructuredDataSchema as React.FC) as React.FC)
